<template>

    <div class="">

        <div :class="['form-group', { 'mb-4': !removeMargin }]">
            <label for="contract_id">
                <strong>
                    {{ title }}
                    <span v-if="required" class="text-danger">*</span>
                </strong>
            </label>

            <v-select
                v-model="model"
                id="contract_id"
                :options="contracts"
                placeholder="Select a Contract"
                label="name"
                :clearable="!isDisabled"
                :disabled="isDisabled"
                @search="fetchOptions"
            ></v-select>

            <input type="hidden" name="contract_id" :value="model?.id ?? ''">

            <div v-if="errors && errors.length > 0" >
                <div class="alert alert-danger mt-2" role="alert" v-for="(error, index) in errors" :key="index">
                    {{ error }}
                </div>
            </div>

        </div>

    </div>

</template>

<script setup>

import { ref, watch, onMounted } from 'vue';
import VSelect from 'vue-select';
import axios from 'axios';
import debounce from 'lodash/debounce';

// Props
const props = defineProps({
    contract: {
        type: Object,
        required: false
    },
    customer: {
        type: Object,
        default: null,
        required: false
    },
    title: {
        type: String,
        default: 'Contract',
        required: false
    },
    errors: {
        type: Array,
        required: false
    },
    disableWhenPassedContract: {
        type: Boolean,
        default: false,
        required: false
    },
    required: {
        type: Boolean,
        default: false,
        required: false
    },
    removeMargin: {
        type: Boolean,
        default: false,
        required: false
    },
});

// Model for two-way binding
const model = defineModel({
    type: Object,
    default: null
});

// Reactive state
const contracts = ref([]);
const isDisabled = ref(false);

// Initialize selectedContract from props if not already set via model
if (!model.value && props.contract) {
    model.value = props.contract;
}

// Emits for backward compatibility
const emit = defineEmits(['update-contract']);

// Methods
const generateContractsRoute = () => {
    return route('api.movements.contracts.select', props.customer ? { customer: props.customer.id } : {});
};

const fetchOptions = (search, loading) => {
    loading(true);
    if (search.length > 2) {
        debounceFetchOptions(search, loading);
    } else {
        loading(false);
    }
};

const debounceFetchOptions = debounce(async (search, loading) => {
    try {
        loading(true);
        const url = generateContractsRoute() + '?search=' + encodeURIComponent(search);
        const { data } = await axios.get(url);
        contracts.value = Array.isArray(data) ? data : Object.values(data);
    } catch (error) {
        loading(false);
        console.log(error);
    } finally {
        loading(false);
    }
}, 500);

const fetchContracts = async () => {
    try {
        const { data } = await axios.get(generateContractsRoute());
        contracts.value = Array.isArray(data) ? data : Object.values(data);

        if (contracts.value.length === 1) {
            model.value = contracts.value[0];
        }

        // If a contract is passed via props, set the model to that contract.
        // This takes precedence over the fetched contracts.
        if (props.contract) {
            model.value = contracts.value.find(contract => contract.id === props.contract.id);
        }
    } catch (error) {
        console.log(error);
    }
};

onMounted(() => {
    fetchContracts();
    if (props.disableWhenPassedContract && props.contract) {
        isDisabled.value = true;
    }
});

// Watch for changes in internalCustomer to refetch contracts
// This is needed on the new Job page so when the customer changes we need to update the contract options accordingly.
watch(() => props.customer, () => {
    fetchContracts();
});

// If the contract prop changes, update the model
watch(() => props.contract, () => {
    model.value = props.contract;
});

// Emit update-contract for backward compatibility
watch(model, (newValue) => {
    if (newValue === null) {
        fetchContracts();
    }
    emit('update-contract', newValue);
});

</script>
